import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import StatusBadge from "@/components/StatusBadge";
import { fileUrl } from "@/lib/files";
import { toast } from "sonner";
import { FileText, Image as ImageIcon } from "lucide-react";

export default function SupplierProductDetail() {
  const { pid } = useParams();
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/products/${pid}`).then((r) => setP(r.data)).catch(()=>toast.error("Not found"));
  useEffect(() => { load(); }, [pid]);

  const submit = async () => {
    setBusy(true);
    try { await api.post(`/supplier/products/${pid}/submit`); toast.success("Submitted for review"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!p) return <DashboardLayout title="Product"><div>Loading…</div></DashboardLayout>;
  const canEdit = ["draft", "rejected"].includes(p.status);
  const canSubmit = p.status === "draft";

  return (
    <DashboardLayout title={p.name} actions={
      <div className="flex gap-2">
        {canEdit && <Link to={`/supplier/products/${p.id}/edit`}><Button variant="outline" data-testid="edit-product-btn">Edit</Button></Link>}
        {canSubmit && <Button data-testid="submit-product-btn" onClick={submit} disabled={busy}>{busy ? "Submitting…" : "Submit for review"}</Button>}
      </div>
    }>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader><CardTitle className="font-heading tracking-tight flex items-center justify-between">Overview <StatusBadge status={p.status} /></CardTitle></CardHeader>
            <CardContent>
              <p className="text-muted-foreground leading-relaxed">{p.description}</p>
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div><div className="overline text-muted-foreground">Category</div><div className="mt-1 text-secondary">{p.category}</div></div>
                <div><div className="overline text-muted-foreground">Origin</div><div className="mt-1 text-secondary">{p.origin_country}</div></div>
                <div><div className="overline text-muted-foreground">HS code</div><div className="mt-1 text-secondary">{p.hs_code || "—"}</div></div>
                <div><div className="overline text-muted-foreground">MOQ</div><div className="mt-1 text-secondary">{p.moq} {p.unit}</div></div>
                <div><div className="overline text-muted-foreground">Price range</div><div className="mt-1 text-secondary">{p.price_min ? `${p.price_min}–${p.price_max || "?"} ${p.currency}` : "—"}</div></div>
                <div><div className="overline text-muted-foreground">Lead time</div><div className="mt-1 text-secondary">{p.lead_time_days ? `${p.lead_time_days} days` : "—"}</div></div>
              </div>
              {p.certifications?.length > 0 && (
                <div className="mt-6">
                  <div className="overline text-muted-foreground mb-2">Certifications</div>
                  <div className="flex flex-wrap gap-2">{p.certifications.map((c) => <Badge key={c} variant="outline">{c}</Badge>)}</div>
                </div>
              )}
            </CardContent>
          </Card>
          {p.admin_notes && (
            <Card className="border-primary/40">
              <CardHeader><CardTitle className="font-heading tracking-tight">Admin notes</CardTitle></CardHeader>
              <CardContent><p className="text-sm text-muted-foreground whitespace-pre-wrap">{p.admin_notes}</p></CardContent>
            </Card>
          )}
        </div>
        <div className="space-y-6">
          <Card>
            <CardHeader><CardTitle className="font-heading tracking-tight flex items-center gap-2"><ImageIcon className="w-4 h-4"/> Images</CardTitle></CardHeader>
            <CardContent>
              {p.image_ids?.length ? (
                <div className="grid grid-cols-2 gap-2">
                  {p.image_ids.map((id) => (<a key={id} href={fileUrl(id)} target="_blank" rel="noreferrer"><img src={fileUrl(id)} alt="" className="w-full h-24 object-cover rounded-md" data-testid={`prd-img-${id}`}/></a>))}
                </div>
              ) : <div className="text-sm text-muted-foreground">No images</div>}
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="font-heading tracking-tight flex items-center gap-2"><FileText className="w-4 h-4"/> Documents</CardTitle></CardHeader>
            <CardContent>
              {p.document_ids?.length ? (
                <ul className="space-y-2 text-sm">
                  {p.document_ids.map((id) => (<li key={id}><a className="text-primary hover:underline break-all" href={fileUrl(id)} target="_blank" rel="noreferrer" data-testid={`prd-doc-${id}`}>Document {id.slice(-6)}</a></li>))}
                </ul>
              ) : <div className="text-sm text-muted-foreground">No documents uploaded</div>}
            </CardContent>
          </Card>
          {p.checklist && Object.keys(p.checklist).length > 0 && (
            <Card>
              <CardHeader><CardTitle className="font-heading tracking-tight">Compliance checklist</CardTitle></CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm">
                  {Object.entries(p.checklist).map(([k, v]) => (
                    <li key={k} className="flex items-center justify-between"><span className="text-muted-foreground">{k}</span><Badge variant="outline">{v ? "OK" : "Missing"}</Badge></li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
